/**
 * SisterSitesBar — thin cross-site nav ribbon rendered above the main
 * Header on IndianFamilyTree.com only.
 *
 * Surfaces IFT visitors to sibling India-focused properties:
 *   - Indias.com    — the India-wide destination portal
 *   - Kerala.com    — flagship state portal
 *   - "Indian States" dropdown — deep links to /state/<slug> pages
 *     already served by IndianFamilyTreeStatePage.
 *
 * The bar is site-aware — it evaluates `resolveSiteSlug()` on mount and
 * silently no-ops for every host that doesn't resolve to
 * `indianfamilytree`. That means the same component can safely live in
 * App.js above <Header /> without leaking on socialpulse.com.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink, MapPin } from "lucide-react";
import { resolveSiteSlug } from "@/config/siteConfig";

// Kept in sync with IndianFamilyTreeStatePage — every slug here has a
// corresponding /state/<slug> landing generated on demand by the
// backend LocationAI encyclopedia.
const INDIAN_STATES = [
  { slug: "andhra-pradesh",    label: "Andhra Pradesh" },
  { slug: "arunachal-pradesh", label: "Arunachal Pradesh" },
  { slug: "assam",             label: "Assam" },
  { slug: "bihar",             label: "Bihar" },
  { slug: "chhattisgarh",      label: "Chhattisgarh" },
  { slug: "delhi",             label: "Delhi" },
  { slug: "goa",               label: "Goa" },
  { slug: "gujarat",           label: "Gujarat" },
  { slug: "haryana",           label: "Haryana" },
  { slug: "himachal-pradesh",  label: "Himachal Pradesh" },
  { slug: "jammu-and-kashmir", label: "Jammu & Kashmir" },
  { slug: "jharkhand",         label: "Jharkhand" },
  { slug: "karnataka",         label: "Karnataka" },
  { slug: "kerala",            label: "Kerala" },
  { slug: "ladakh",            label: "Ladakh" },
  { slug: "madhya-pradesh",    label: "Madhya Pradesh" },
  { slug: "maharashtra",       label: "Maharashtra" },
  { slug: "manipur",           label: "Manipur" },
  { slug: "meghalaya",         label: "Meghalaya" },
  { slug: "mizoram",           label: "Mizoram" },
  { slug: "nagaland",          label: "Nagaland" },
  { slug: "odisha",            label: "Odisha" },
  { slug: "puducherry",        label: "Puducherry" },
  { slug: "punjab",            label: "Punjab" },
  { slug: "rajasthan",         label: "Rajasthan" },
  { slug: "sikkim",            label: "Sikkim" },
  { slug: "tamil-nadu",        label: "Tamil Nadu" },
  { slug: "telangana",         label: "Telangana" },
  { slug: "tripura",           label: "Tripura" },
  { slug: "uttar-pradesh",     label: "Uttar Pradesh" },
  { slug: "uttarakhand",       label: "Uttarakhand" },
  { slug: "west-bengal",       label: "West Bengal" },
];

export default function SisterSitesBar() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Detect once — the site slug doesn't change during the SPA session.
  const { isIft, isMftCare } = useMemo(() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      const slug = (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase();
      return {
        isIft: slug.includes("indianfamilytree"),
        isMftCare: slug === "myfamilytreecare" || slug === "myfamilytree",
      };
    } catch { return { isIft: false, isMftCare: false }; }
  }, []);

  // Close the dropdown on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!isIft && !isMftCare) return null;

  // Palette + label swap so the ribbon feels native on each host.
  const barBg = isMftCare ? "#0E7C55" /* heritage green */ : "#5C2E0A" /* deep bark */;
  const introLabel = isMftCare ? "Sister sites:" : "Explore the Indias network:";

  // The 4th sibling chip changes per host — on IFT we link out to
  // MyFamilyTree.care; on MyFamilyTree.care we link out to IFT so
  // visitors can hop between the two family/heritage properties.
  const crossFamilyChip = isMftCare
    ? { href: "https://indianfamilytree.com", label: "IndianFamilyTree.com", testid: "ift-sister-ift" }
    : { href: "https://myfamilytree.care", label: "MyFamilyTree.care", testid: "ift-sister-mftcare" };

  return (
    <div
      className="w-full text-white text-xs"
      style={{ background: barBg }}
      data-testid="ift-sister-sites-bar"
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="opacity-70 hidden sm:inline">{introLabel}</span>
        <a
          href="https://indias.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:underline"
          data-testid="ift-sister-indias"
        >
          Indias.com <ExternalLink className="w-3 h-3 opacity-70" />
        </a>
        <a
          href="https://kerala.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:underline"
          data-testid="ift-sister-kerala"
        >
          Kerala.com <ExternalLink className="w-3 h-3 opacity-70" />
        </a>
        <a
          href="https://naturepulse.cloud"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:underline"
          data-testid="ift-sister-naturepulse"
        >
          NaturePulse <ExternalLink className="w-3 h-3 opacity-70" />
        </a>
        <a
          href={crossFamilyChip.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:underline"
          data-testid={crossFamilyChip.testid}
        >
          {crossFamilyChip.label} <ExternalLink className="w-3 h-3 opacity-70" />
        </a>

        {/* Indian States dropdown — links deep into /state/<slug>. */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 hover:underline"
            aria-haspopup="menu"
            aria-expanded={open ? "true" : "false"}
            data-testid="ift-sister-states-toggle"
          >
            <MapPin className="w-3 h-3 opacity-80" /> Indian States
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div
              role="menu"
              className="absolute left-0 top-full mt-1 w-[520px] max-w-[calc(100vw-2rem)] rounded-xl shadow-xl border p-3 z-[95] text-slate-800"
              style={{ background: "#FFFDF8", borderColor: "#E8DFCF" }}
              data-testid="ift-sister-states-menu"
            >
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-2">
                Explore state family histories
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
                {INDIAN_STATES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/state/${s.slug}`}
                    onClick={() => setOpen(false)}
                    className="text-xs py-1 hover:underline"
                    data-testid={`ift-sister-state-${s.slug}`}
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
